import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';
import { onUpgrade402 } from '../utils/onUpgrade402';

/**
 * Playlist → wall assignments (Phase 3.4 + Phase 6 of the restaurant-chain
 * platform redesign). Backs the Content Library "Assign" UI and the visual
 * weekly Scheduler.
 *   GET    /assignments[?wall_id=&group_id=&playlist_id=]  → { assignments }
 *   POST   /assignments                          (playlist_id, wall_id|group_id, schedule)
 *   PUT    /assignments/{id}                      (playlist_id?, schedule fields)
 *   DELETE /assignments/{id}
 *
 * Backend wraps in a Laravel resource ({ data: { status, ... } }); unwrap with
 * `res?.data ?? res`.
 */
const LIST_KEY = 'wallAssignments';

export const useGetAssignments = (params = {}) =>
  useQuery([LIST_KEY, params], async () => {
    const res = (await axiosInstance.get('/assignments', { params })).data;
    return (res?.data ?? res)?.assignments ?? [];
  }, { staleTime: 10_000 });

export const useCreateAssignment = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/assignments', payload), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); showToast('success', 'Assignments', 'Playlist assigned.'); },
    onError: (err) => {
      if (onUpgrade402(err, showToast)) return;
      showToast('error', 'Assignments', err.response?.data?.message);
    },
  });
};

export const useUpdateAssignment = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ id, ...payload }) => axiosInstance.put(`/assignments/${id}`, payload), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); showToast('success', 'Assignments', 'Schedule updated.'); },
    onError: (err) => {
      if (onUpgrade402(err, showToast)) return;
      showToast('error', 'Assignments', err.response?.data?.message);
    },
  });
};

export const useDeleteAssignment = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/assignments/${id}`), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); showToast('info', 'Assignments', 'Assignment removed.'); },
    onError: (err) => showToast('error', 'Assignments', err.response?.data?.message),
  });
};

/**
 * Bulk-apply one wall's/group's whole schedule to other walls/groups (REPLACE).
 *   POST /assignments/copy  { from:{type,id}, to:[{type,id}], mode:'replace' }
 */
export const useCopyAssignment = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/assignments/copy', payload), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); showToast('success', 'Assignments', 'Schedule copied.'); },
    onError: (err) => {
      if (onUpgrade402(err, showToast)) return;
      showToast('error', 'Assignments', err.response?.data?.message);
    },
  });
};
